/**
 * @file value.tsx
 *
 * @fileoverview Renders information grouped on tabs.
 */
import { ReactNode } from "react";
import { Root, List, Content, Trigger } from "@radix-ui/react-tabs";
import styles from "./tabs.module.scss";
import { cva } from "class-variance-authority";

const tabsRoot = cva([styles.tabsRoot], {
  variants: {
    tabsListItemFull: {
      true: styles.tabsRootListItemFull,
    },
    tabEqualSpace: {
      true: styles.tabEqualSpace,
    },
    hardBackground: {
      true: styles.tabsRootHardBg,
    },
    hardBackgroundColor: {
      primary: null,
      secondary: null,
    },
  },
  compoundVariants: [
    {
      hardBackgroundColor: "primary",
      hardBackground: true,
      class: styles.tabsRootHardBgPrimary,
    },
    {
      hardBackgroundColor: "secondary",
      hardBackground: true,
      class: styles.tabsRootHardBgSecondary,
    },
  ],
});

export interface TabsProps {
  /**
   * The content to display inside tabs.
   */
  children: ReactNode;
  /**
   * If the tabs list background should have a hard color (primary, secondary).
   *
   * @default "true"
   */
  hardBackground?: boolean;
  /**
   * The color for the hard background on the tab list.
   *
   * @default "primary"
   */
  hardBackgroundColor?: "primary" | "secondary";
  /**
   * If the tabs should occypy the whole container.
   *
   * @default "true";
   */
  tabsListItemFull?: boolean;
  /**
   * If each tab should occupy the same amount of space.
   *
   * @default "true";
   */
  tabEqualSpace?: boolean;
  /**
   * On change event for tabs
   */
  onChange?: (value) => void;
  /**
   * The value that is selected.
   */
  value?: string;
  /**
   * Default value when value is not provided.
   */
  defaultValue?: string;
}

export default function Tabs({
  tabsListItemFull = true,
  hardBackground = true,
  tabEqualSpace = true,
  hardBackgroundColor = "primary",
  children,
  value,
  defaultValue,
  onChange,
}: TabsProps) {
  return (
    <Root
      defaultValue={defaultValue}
      value={value}
      onValueChange={onChange}
      className={tabsRoot({
        tabsListItemFull,
        tabEqualSpace,
        hardBackground,
        hardBackgroundColor,
      })}
    >
      {children}
    </Root>
  );
}

export { List as TabList, Content as TabPanel, Trigger as Tab };

export { Tabs };
